import { ErrorCode } from '@ps/web-core-enums';
import { StarsButton as Button, NumericPIN, PIN } from '@ps/web-shared-components';
import { createForm } from '@ps/web-shared-ui';
import type { FormApi } from 'final-form';
import { FORM_ERROR } from 'final-form';
import type { SyntheticEvent } from 'react';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import ResendButton from '@swf/components/ResendButton/ResendButton';
import useSearchParams from '@swf/core/hooks/useSearchParams';
import type { LoginPayload } from '@swf/core/models/auth/login';
import type { Dispatch, RootState } from '@swf/core/store';

import ErrorMessage from '../ErrorMessage/ErrorMessage';
import NextButton from '../NextButton/NextButton';
import PINFormText from '../PINFormText/PINFormText';

import { TXTWCL_Resend_code } from './PINForm.messages.json';
import styles from './PinForm.scss';

const RESEND_TIMER_IN_SECONDS = 900;

interface FormValues {
  pin: string;
}

const Form = createForm<FormValues>();

const PINForm: React.FC = () => {
  const {
    auth: { login, pinReset }
  } = useDispatch<Dispatch>();
  const searchParams = useSearchParams<LoginPayload & { errorCode: ErrorCode }>();
  const { push } = useHistory();
  const loading = useSelector((rootState: RootState) => !!rootState.loading?.models.auth);
  const isTempPin =
    searchParams.errorCode === ErrorCode.TEMP_PIN_REQUIRED ||
    searchParams.errorCode === ErrorCode.TEMP_PIN_REQUIRED_SMS;
  const [countdown, setCountdown] = useState(10);

  const handleSubmit = async ({ pin }: FormValues, formApi: FormApi<FormValues>) => {
    const errorCode = await login({ ...searchParams, pin });
    formApi.change('pin', undefined);

    if (errorCode) {
      return { [FORM_ERROR]: 'ERROR_STATUS_CODE' };
    }

    return {};
  };

  const handleClick = async (e: SyntheticEvent, stayOnPage?: boolean) => {
    e.preventDefault();
    stayOnPage && setCountdown(RESEND_TIMER_IN_SECONDS);
    await pinReset({ userId: searchParams.userId });
    !stayOnPage && push({ pathname: '/auth/login/pin-reset' });
  };

  return (
    <Form
      onSubmit={handleSubmit}
      render={({ pristine }) => (
        <>
          <ErrorMessage />
          <PINFormText errorCode={searchParams.errorCode} handleClick={handleClick}>
            {isTempPin ? (
              <NumericPIN className={styles.numericPin} id="pin" name="pin" label="" />
            ) : (
              <PIN id="pin" name="pin" />
            )}
            <NextButton
              className={styles.next}
              block
              type="submit"
              loading={loading}
              disabled={pristine}
            />
            {isTempPin &&
              (countdown <= 0 ? (
                <Button
                  block
                  variant="secondary"
                  type="button"
                  translation={TXTWCL_Resend_code}
                  onClick={(e) => handleClick(e, true)}
                />
              ) : (
                <ResendButton
                  block
                  countdown={countdown}
                  setCountdown={setCountdown}
                  hideHoursFormat
                />
              ))}
          </PINFormText>
        </>
      )}
    />
  );
};

export default PINForm;
